/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import { FiPlus, FiEdit2, FiDollarSign } from 'react-icons/fi';

interface ProjectBudgetTabProps {
  project: any;
  budgetUsagePercent: number;
  projectCategories: any[];
  projectExpenses: any[];
  projectRevenues: any[];
  totalRevenue: number;
  setIsCategoryModalOpen: (val: boolean) => void;
  setEditingCategory: (cat: any) => void;
  setCatEditName: (val: string) => void;
  setCatEditAllocated: (val: string) => void;
  setCatEditNotes: (val: string) => void;
  setIsEditBudgetModalOpen: (val: boolean) => void;
  setTxType: (type: 'Expense' | 'Revenue') => void;
  setIsTxModalOpen: (val: boolean) => void;
}

export const ProjectBudgetTab: React.FC<ProjectBudgetTabProps> = ({
  project,
  budgetUsagePercent,
  projectCategories,
  projectExpenses,
  projectRevenues,
  totalRevenue,
  setIsCategoryModalOpen,
  setEditingCategory,
  setCatEditName,
  setCatEditAllocated,
  setCatEditNotes,
  setIsEditBudgetModalOpen,
  setTxType,
  setIsTxModalOpen,
}) => {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-white/5 bg-[#111111] p-8 text-center py-16">
      <div className="w-12 h-12 rounded-2xl bg-[#1A1A1A] border border-white/5 flex items-center justify-center text-zinc-400 mb-4">
        <FiDollarSign className="w-6 h-6 text-zinc-400 animate-pulse" />
      </div>
      <h3 className="text-white text-base font-bold mb-1">Budget Tracker</h3>
      <p className="text-zinc-550 text-sm max-w-sm">Coming Soon</p>
    </div>
  );

  /* Commented out original UI - to be done eventually
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Left Column (3/5) * /}
      <div className="lg:col-span-3 space-y-6">
        {/* Budget Overview Cards * /}
        <div>
          <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">
            Budget Overview
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-4">
              <p className="text-zinc-555 text-[9px] font-bold uppercase tracking-wider">
                Allocated Budget
              </p>
              <p className="text-white text-lg font-bold mt-1.5">
                ${project.allocatedBudget.toLocaleString()}
              </p>
              <p className="text-zinc-555 text-[9px] mt-0.5">Total budget</p>
            </div>
            <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-4">
              <p className="text-zinc-555 text-[9px] font-bold uppercase tracking-wider">Spent</p>
              <p className="text-white text-lg font-bold mt-1.5">
                ${project.spentBudget.toLocaleString()}
              </p>
              <p className="text-zinc-555 text-[9px] mt-0.5">{budgetUsagePercent}% of budget</p>
            </div>
            <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-4">
              <p className="text-zinc-555 text-[9px] font-bold uppercase tracking-wider">
                Remaining
              </p>
              <p className="text-emerald-400 text-lg font-bold mt-1.5">
                ${Math.max(0, project.allocatedBudget - project.spentBudget).toLocaleString()}
              </p>
              <p className="text-zinc-555 text-[9px] mt-0.5">Available</p>
            </div>
            <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-4">
              <p className="text-zinc-555 text-[9px] font-bold uppercase tracking-wider">
                Budget Usage
              </p>
              <p className="text-white text-lg font-bold mt-1.5">{budgetUsagePercent}%</p>
              <p className="text-zinc-555 text-[9px] mt-0.5">Utilization</p>
            </div>
          </div>

          <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-5">
            <div className="flex items-center justify-between text-xs font-semibold mb-2">
              <span className="text-zinc-400">Overall utilization</span>
              <span className="text-emerald-400">{budgetUsagePercent}%</span>
            </div>
            <div className="w-full h-2.5 bg-zinc-850 rounded-full overflow-hidden mb-2">
              <div
                className="h-full bg-emerald-500 rounded-full"
                style={{
                  width: `${budgetUsagePercent}%`,
                }}
              />
            </div>
            <div className="flex items-center justify-between text-[9px] text-zinc-500 font-bold uppercase tracking-wider mt-1">
              <span>$0</span>
              <span>${project.allocatedBudget.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Budget Categories section * /}
        <div className="bg-[#0f0f0f] border border-white/5 rounded-3xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">
              Budget Categories
            </h3>
            {!project.isArchived && (
              <button
                onClick={() => setIsCategoryModalOpen(true)}
                className="flex items-center gap-1 px-4 py-1.5 rounded-full border border-white/10 bg-zinc-900/60 hover:bg-white/5 text-zinc-300 text-xs font-bold cursor-pointer transition-colors"
              >
                <FiPlus className="w-3.5 h-3.5" />
                <span>Add Category</span>
              </button>
            )}
          </div>

          <div className="space-y-5">
            {projectCategories.map((cat) => {
              const utilPercent =
                cat.allocated > 0 ? Math.round((cat.spent / cat.allocated) * 100) : 0;
              const remaining = Math.max(0, cat.allocated - cat.spent);

              return (
                <div key={cat.id} className="relative group">
                  <div className="flex items-center justify-between text-xs font-bold mb-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: cat.dotColor }}
                      />
                      <span className="text-white">{cat.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-zinc-300">
                        ${cat.spent.toLocaleString()} / ${cat.allocated.toLocaleString()}
                      </span>
                      <span className="text-emerald-400 font-semibold text-[10px]">
                        ${remaining.toLocaleString()} left
                      </span>
                      <span className="text-zinc-550 w-8 text-right">{utilPercent}%</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-zinc-850 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${Math.min(utilPercent, 100)}%`,
                          backgroundColor: cat.dotColor,
                        }}
                      />
                    </div>
                    {!project.isArchived && (
                      <button
                        onClick={() => {
                          setEditingCategory(cat);
                          setCatEditName(cat.name);
                          setCatEditAllocated(cat.allocated.toString());
                          setCatEditNotes(cat.notes || '');
                          setIsEditBudgetModalOpen(true);
                        }}
                        className="p-1 rounded bg-zinc-900 border border-white/5 hover:bg-white/5 opacity-0 group-hover:opacity-100 hover:text-white text-zinc-550 transition-all cursor-pointer"
                      >
                        <FiEdit2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right Column (2/5) * /}
      <div className="lg:col-span-2 space-y-6">
        {/* Recent Expenses card * /}
        <div className="bg-[#0f0f0f] border border-white/5 rounded-3xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">
              Recent Expenses
            </h3>
            {!project.isArchived && (
              <button
                onClick={() => {
                  setTxType('Expense');
                  setIsTxModalOpen(true);
                }}
                className="text-zinc-400 hover:text-white text-xs font-bold flex items-center gap-1 cursor-pointer transition-colors"
              >
                <FiPlus className="w-3 h-3" /> Add
              </button>
            )}
          </div>

          <div className="space-y-4">
            {projectExpenses.map((exp) => (
              <div key={exp.id} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-zinc-900 border border-white/5 text-zinc-400 flex items-center justify-center">
                    <FiDollarSign className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-white text-xs font-bold">{exp.title}</p>
                    <p className="text-zinc-550 text-[10px] mt-0.5">{exp.date}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white text-xs font-bold">${exp.amount.toLocaleString()}</p>
                  <span className="inline-block text-[8px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 mt-1">
                    {exp.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Revenue card * /}
        <div className="bg-[#0f0f0f] border border-white/5 rounded-3xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Revenue</h3>
            {!project.isArchived && (
              <button
                onClick={() => {
                  setTxType('Revenue');
                  setIsTxModalOpen(true);
                }}
                className="text-zinc-400 hover:text-white text-xs font-bold flex items-center gap-1 cursor-pointer transition-colors"
              >
                <FiPlus className="w-3.5 h-3.5" /> Add
              </button>
            )}
          </div>

          <div className="space-y-4">
            {projectRevenues.map((rev) => (
              <div key={rev.id} className="flex items-center justify-between">
                <div>
                  <p className="text-white text-xs font-bold">{rev.title}</p>
                  <p className="text-zinc-550 text-[9px] mt-1 flex items-center gap-1">
                    <span className="bg-white/5 px-2 py-0.5 rounded border border-white/5 text-[8px] uppercase font-bold text-zinc-400">
                      {rev.category}
                    </span>
                    <span>• {rev.date}</span>
                  </p>
                </div>
                <span className="text-emerald-450 text-xs font-bold">
                  +${rev.amount.toLocaleString()}
                </span>
              </div>
            ))}

            <div className="flex items-center justify-between pt-4 border-t border-white/5 font-bold text-xs">
              <span className="text-zinc-500">Total revenue</span>
              <span className="text-emerald-450 text-sm">+${totalRevenue.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Spend by Category breakdown list * /}
        <div className="bg-[#0f0f0f] border border-white/5 rounded-3xl p-6">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-5">
            Spend by Category
          </h3>
          <div className="space-y-4.5">
            {projectCategories.map((cat) => {
              const totalSpent = project.spentBudget || 1;
              const percent = Math.round((cat.spent / totalSpent) * 100);

              return (
                <div
                  key={cat.id}
                  className="flex items-center justify-between text-xs font-semibold"
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: cat.dotColor }}
                    />
                    <span className="text-zinc-300">{cat.name}</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-16 h-1 bg-zinc-850 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${percent}%`, backgroundColor: cat.dotColor }}
                      />
                    </div>
                    <span className="text-white w-8 text-right">{percent}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
  */
};
